import React from 'react';
import Leaderboard from '../components/Leaderboard';
import { useZenCoins } from '../hooks/useZenCoins';
import { useLanguage } from '../contexts/LanguageContext';
import GlassCard from '../components/ui/GlassCard';

const Rewards = () => {
    const { balance } = useZenCoins();
    const { t } = useLanguage();

    const rewardsList = [
        { id: 1, titleKey: 'premiumDarkTheme', cost: 500, icon: '🌙', type: 'theme', color: '#6366f1' },
        { id: 2, titleKey: 'masterBadge', cost: 1000, icon: '🏆', type: 'badge', color: '#f59e0b' },
        { id: 3, titleKey: 'oneOnOneMentor', cost: 5000, icon: '👨‍🏫', type: 'session', color: '#27b05b' },
        { id: 4, titleKey: 'adFreeExperience', cost: 2000, icon: '🚫', type: 'feature', color: '#ef4444' },
    ];

    return (
        <div style={styles.pageWrapper}>
            <div className="container">

                {/* Header with Balance */}
                <div style={styles.banner}>
                    <div style={styles.bannerContent}>
                        <div>
                            <h1 style={styles.bannerTitle}>{t('rewardsHub') || 'Rewards Hub'}</h1>
                            <p style={styles.bannerSubtitle}>{t('redeemYourCoins') || 'Redeem your ZenCoins for premium perks'}</p>
                        </div>
                        <div style={styles.balanceCard}>
                            <div style={styles.balanceLabel}>{t('currentBalance') || 'Current Balance'}</div>
                            <div style={styles.balanceValue}>
                                🪙 {balance.toLocaleString()}
                            </div>
                        </div>
                    </div>
                </div>

                <div style={styles.contentGrid}>

                    {/* Rewards Section */}
                    <div>
                        <h2 style={styles.sectionTitle}>{t('redeemRewards') || 'Available Rewards'}</h2>
                        <div style={styles.rewardsGrid}>
                            {rewardsList.map(reward => (
                                <GlassCard key={reward.id} hoverEffect={true} style={styles.rewardCard}>
                                    <div style={{ ...styles.rewardIcon, backgroundColor: `${reward.color}15`, color: reward.color }}>
                                        {reward.icon}
                                    </div>
                                    <h3 style={styles.rewardTitle}>{t(reward.titleKey) || reward.titleKey}</h3>
                                    <div style={styles.rewardCost}>
                                        🪙 {reward.cost}
                                    </div>
                                    <button style={{ ...styles.redeemButton, backgroundColor: reward.color }}>
                                        {t('redeem') || 'Redeem'}
                                    </button>
                                </GlassCard>
                            ))}
                        </div>

                        {/* Referral Support */}
                        <div style={styles.referralCard}>
                            <div style={styles.referralIcon}>🤝</div>
                            <div style={{ flex: 1 }}>
                                <h3 style={styles.referralTitle}>{t('inviteFriends') || 'Invite Friends'}</h3>
                                <p style={styles.referralText}>{t('inviteFriendsDesc') || 'Earn 100 ZC for every friend who joins!'}</p>
                            </div>
                            <button style={styles.copyButton}>{t('copyLink') || 'Copy Link'}</button>
                        </div>
                    </div>

                    {/* Sidebar: Leaderboard */}
                    <div style={styles.sidebar}>
                        <h2 style={styles.sectionTitle}>{t('topLearners') || 'Top Learners'}</h2>
                        <Leaderboard />
                        
                        <GlassCard style={{ marginTop: '30px' }}>
                            <h3 style={{ ...styles.rewardTitle, marginBottom: '10px' }}>How to earn?</h3>
                            <ul style={{ paddingLeft: '20px', color: '#64748b' }}>
                                <li>Complete lessons (+10 ZC)</li>
                                <li>Daily check-in (+5 ZC)</li>
                                <li>Practice sessions (+2 ZC)</li>
                            </ul>
                        </GlassCard>
                    </div>

                </div>
            </div>
        </div>
    );
};

const styles = {
    pageWrapper: {
        background: 'transparent',
        minHeight: '100vh',
        paddingBottom: '80px',
        paddingTop: '20px'
    },
    banner: {
        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '24px',
        padding: '40px',
        color: 'white',
        marginBottom: '40px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
    },
    bannerContent: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '20px'
    },
    bannerTitle: {
        fontSize: '2.5rem',
        fontWeight: '800',
        margin: 0,
        letterSpacing: '-0.03em'
    },
    bannerSubtitle: {
        fontSize: '1.1rem',
        color: '#94a3b8',
        marginTop: '8px'
    },
    balanceCard: {
        background: 'rgba(255,255,255,0.05)',
        padding: '20px 30px',
        borderRadius: '20px',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        textAlign: 'right'
    },
    balanceLabel: {
        color: '#94a3b8',
        fontSize: '0.9rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em'
    },
    balanceValue: {
        fontSize: '2.2rem',
        fontWeight: '800',
        color: '#fbbf24',
        textShadow: '0 0 20px rgba(251, 191, 36, 0.3)'
    },
    contentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 340px',
        gap: '40px'
    },
    sectionTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '24px'
    },
    rewardsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '24px'
    },
    rewardCard: {
        textAlign: 'center',
        padding: '30px'
    },
    rewardIcon: {
        fontSize: '3rem',
        width: '80px',
        height: '80px',
        borderRadius: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 20px'
    },
    rewardTitle: {
        fontSize: '1.1rem',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '10px'
    },
    rewardCost: {
        fontSize: '1.2rem',
        fontWeight: '800',
        color: '#b45309',
        background: '#fffbeb',
        padding: '4px 12px',
        borderRadius: '12px',
        display: 'inline-block',
        marginBottom: '20px'
    },
    redeemButton: {
        display: 'block',
        width: '100%',
        padding: '12px',
        color: 'white',
        border: 'none',
        borderRadius: '12px',
        fontWeight: '700',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    },
    referralCard: {
        marginTop: '40px',
        background: 'white',
        borderRadius: '24px',
        padding: '40px',
        display: 'flex',
        alignItems: 'center',
        gap: '30px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
        flexWrap: 'wrap'
    },
    referralIcon: {
        fontSize: '4rem'
    },
    referralTitle: {
        fontSize: '1.5rem',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '8px'
    },
    referralText: {
        color: '#64748b',
        fontSize: '1.1rem'
    },
    copyButton: {
        padding: '14px 28px',
        background: '#f8fafc',
        border: '1px solid #e2e8f0',
        color: '#27b05b',
        fontWeight: '700',
        borderRadius: '12px',
        cursor: 'pointer'
    },
    sidebar: {
        display: 'flex',
        flexDirection: 'column'
    }
};

export default Rewards;
